import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { Checkbox } from 'primereact/checkbox';
import { CustomTag } from '../../../../../components/CustomTag/CustomTag';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { Calendar } from 'primereact/calendar';
import { TriStateCheckbox } from 'primereact/tristatecheckbox';
import {
  ExamSteps,
  getExamStatusDisplayText,
} from '../../../../../api/types/reports/education/exams/report-exam-statuses';
import { ReportExamSideBarFilterDatamodel } from './report-exam-side-bar-filter-datamodel';
import { Button } from 'primereact/button';

interface YearOption {
  year: string;
  value: number;
}

interface StatusOption {
  name: string;
  severity: 'success' | 'warning' | 'danger';
}

interface ExamSession {
  sessionName: string;
}

export interface ReportExamSideBarFilterProps {
  onSearch: (filters: ReportExamSideBarFilterDatamodel) => void;
}

export const ReportExamSideBarFilter: React.FC<ReportExamSideBarFilterProps> =
  observer(({ onSearch }) => {
    const examSteps = [
      ExamSteps.ASSIGNMENTS,
      ExamSteps.WAITING_FOR_QUESTIONNAIRE,
      ExamSteps.WAITING_FOR_EXAM,
      ExamSteps.EXAM_STARTED,
      ExamSteps.EXAM_COMPLETED,
      ExamSteps.NOTEBOOKS_COLLECTED_FOR_REVIEW,
      ExamSteps.GRADES_ENTERED,
      ExamSteps.GRADES_PUBLISHED,
    ];

    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedExamTypes, setSelectedExamTypes] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
    const [selectedSteps, setSelectedSteps] = useState<
      Record<ExamSteps, boolean | null>
    >(() => {
      const initialSteps: Record<ExamSteps, boolean | null> = {} as Record<
        ExamSteps,
        boolean | null
      >;
      examSteps.forEach((step) => {
        initialSteps[step] = null;
      });
      return initialSteps;
    });
    const [examDateStart, setExamDateStart] = useState<Date | null>(null);
    const [examDateEnd, setExamDateEnd] = useState<Date | null>(null);

    // Generate years array
    const years = Array.from({ length: 10 }, (_, i) => ({
      year: `${2020 + i} - ${2021 + i}`,
      value: 2020 + i,
    }));

    const examSessions: ExamSession[] = ['א׳', 'ב׳', 'ג׳', 'ד׳'].map(
      (session) => ({
        sessionName: session,
      }),
    );

    const courseStatuses = [
      { name: 'פעיל', severity: 'success' as const },
      { name: 'בהתראה', severity: 'warning' as const },
      { name: 'בהשעיה', severity: 'danger' as const },
    ];

    const handleSearch = () => {
      onSearch({
        branchesIds: selectedBranches,
        programIds: selectedPrograms,
        startYears: selectedYears,
        examTypes: selectedExamTypes,
        statuses: selectedStatuses,
        sessions: selectedSessions,
        steps: selectedSteps,
        examDateStart,
        examDateEnd,
      });
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      filterSection: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px 0',
      },
      filterItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        direction: 'rtl' as const,
      },
      buttonContainer: {
        marginTop: 'auto',
        paddingTop: '16px',
      },
      searchButton: {
        width: '100%',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={(branchId) => {
              const newBranches = selectedBranches.includes(branchId)
                ? selectedBranches.filter((b) => b !== branchId)
                : [...selectedBranches, branchId];
              setSelectedBranches(newBranches);
            }}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תכנית לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={(programId) => {
              const newPrograms = selectedPrograms.includes(programId)
                ? selectedPrograms.filter((p) => p !== programId)
                : [...selectedPrograms, programId];
              setSelectedPrograms(newPrograms);
            }}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={(yearStr) => {
              const year = parseInt(yearStr);
              const newYears = selectedYears.includes(year)
                ? selectedYears.filter((y) => y !== year)
                : [...selectedYears, year];
              setSelectedYears(newYears);
            }}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander>
            <div>סטטוס קורס</div>
            <div style={styles.filterSection}>
              {courseStatuses.map((status) => (
                <div key={status.name} style={styles.filterItem}>
                  <Checkbox
                    checked={selectedStatuses.includes(status.name)}
                    onChange={() => {
                      const newStatuses = selectedStatuses.includes(status.name)
                        ? selectedStatuses.filter((s) => s !== status.name)
                        : [...selectedStatuses, status.name];
                      setSelectedStatuses(newStatuses);
                    }}
                  />
                  <CustomTag
                    width={'70px'}
                    text={status.name}
                    severity={status.severity}
                  />
                </div>
              ))}
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="מועד"
            items={examSessions}
            selectedItems={selectedSessions}
            onItemToggle={(sessionName) => {
              const newSessions = selectedSessions.includes(sessionName)
                ? selectedSessions.filter((s) => s !== sessionName)
                : [...selectedSessions, sessionName];
              setSelectedSessions(newSessions);
            }}
            getItemLabel={(session) => session.sessionName}
            getItemKey={(session) => session.sessionName}
            searchPlaceholder="חפש מועד..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סוג מבחן"
            items={globalSettingsStores.examTypes}
            selectedItems={selectedExamTypes}
            onItemToggle={(examType) => {
              const newExamTypes = selectedExamTypes.includes(examType)
                ? selectedExamTypes.filter((e) => e !== examType)
                : [...selectedExamTypes, examType];
              setSelectedExamTypes(newExamTypes);
            }}
            getItemLabel={(examType) => examType.examTypeName}
            getItemKey={(examType) => examType.examTypeName}
            searchPlaceholder="חפש סוג מבחן..."
          />

          <CustomExpander>
            <div>שלב הבחינה</div>
            <div style={styles.filterSection}>
              {examSteps.map((step) => (
                <div key={step} style={styles.filterItem}>
                  <TriStateCheckbox
                    value={selectedSteps[step]}
                    onChange={(e) => {
                      let value: boolean | null = null;
                      if (e.value === true || e.value === false) {
                        value = e.value;
                      }
                      setSelectedSteps({
                        ...selectedSteps,
                        [step]: value,
                      });
                    }}
                  />
                  <span>{getExamStatusDisplayText(step)}</span>
                </div>
              ))}
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>תאריך בחינה</div>
            <div style={styles.filterSection}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <Calendar
                  value={examDateStart}
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  onChange={(e) => setExamDateStart(e.value as Date)}
                  placeholder="מתאריך"
                  showIcon
                />
                <Calendar
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  value={examDateEnd}
                  onChange={(e) => setExamDateEnd(e.value as Date)}
                  placeholder="עד תאריך"
                  showIcon
                />
              </div>
            </div>
          </CustomExpander>

          <div style={styles.buttonContainer}>
            <Button
              label="חפש"
              icon="pi pi-search"
              onClick={handleSearch}
              style={styles.searchButton}
            />
          </div>
        </div>
      </div>
    );
  });
